'use client';

import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { motion, useScroll, useMotionValueEvent, AnimatePresence } from 'framer-motion';
import CtaButton from '@/components/ui/CtaButton';

const HomeIcon = ({ className }: { className?: string }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden
  >
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);

type NavItem = {
  href: string;
  label: string;
  home?: boolean;
};

const NAV_ITEMS: NavItem[] = [
  { href: '/', label: 'Strona główna', home: true },
  { href: '/o-nas', label: 'ZESPÓŁ' },
  { href: '/eventy', label: 'EVENTY' },
  { href: '/wyjazdy', label: 'WYJAZDY' },
  { href: '/konferencje', label: 'KONFERENCJE' },
  { href: '/atrakcje', label: 'ATRAKCJE' },
];

function NavItemLink({
  item,
  className,
  onClick,
}: {
  item: NavItem;
  className: string;
  onClick?: () => void;
}) {
  if (item.home) {
    return (
      <Link
        href={item.href}
        scroll
        onClick={onClick}
        aria-label={item.label}
        className={`inline-flex items-center justify-center ${className}`}
      >
        <HomeIcon />
      </Link>
    );
  }

  return (
    <Link href={item.href} scroll onClick={onClick} className={className}>
      {item.label}
    </Link>
  );
}

export default function Navbar() {
  const pathname = usePathname();
  const isHomePage = pathname === '/';

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [isAtTop, setIsAtTop] = useState(true);
  // Nowy stan: czy sekcja Hero jest obecnie widoczna na ekranie
  const [isOverHero, setIsOverHero] = useState(true);

  const { scrollY } = useScroll();

  // 1. Śledzenie sekcji Hero za pomocą Intersection Observer
  useEffect(() => {
    if (!isHomePage) {
      setIsOverHero(false);
      return;
    }

    const heroElement = document.getElementById('hero');
    if (!heroElement) return;

    const observer = new IntersectionObserver(
        ([entry]) => {
          // Jeśli Hero jest widoczne choćby w 1%, ustawiamy isOverHero na true
          setIsOverHero(entry.isIntersecting);
        },
        {
          // Próg 0 oznacza: reaguj natychmiast, gdy tylko pierwszy/ostatni piksel Hero wejdzie na ekran
          threshold: 0,
          // Opcjonalnie: margines ujemny, jeśli chcesz by znikał jeszcze szybciej przed stykiem
          rootMargin: "-10px 0px 0px 0px"
        }
    );

    observer.observe(heroElement);
    return () => observer.disconnect();
  }, [isHomePage]);

  // 2. Logika kierunku skrollowania (dla pozostałej części strony)
  useMotionValueEvent(scrollY, "change", (latest) => {
    const previous = scrollY.getPrevious() ?? 0;
    const diff = latest - previous;

    setIsAtTop(latest < 50);

    if (latest < 50) {
      setIsVisible(true);
    } else if (diff > 5) { // Skroll w dół
      setIsVisible(false);
    } else if (diff < -5) { // Skroll w górę
      setIsVisible(true);
    }
  });

  // KLUCZOWA LOGIKA:
  // Ukryj, jeśli menu jest zamknięte ORAZ (jesteśmy nad sekcją Hero LUB skrollujemy w dół)
  const shouldHide = !isMobileMenuOpen && (
      (isHomePage && isOverHero) || (!isVisible && !isAtTop)
  );

  return (
      <motion.header
          className={`fixed rounded-b-3xl top-0 left-0 w-full py-2 lg:px-[2%] z-[9999] 
        ${isOverHero && !isMobileMenuOpen ? 'bg-transparent' : 'bg-primary'}
        ${shouldHide ? 'pointer-events-none' : 'pointer-events-auto'}`}
          initial={false}
          animate={{
            y: shouldHide ? -180 : 0,
            opacity: shouldHide ? 0 : 1
          }}
          transition={{
            duration: 0.3,
            ease: [0.23, 1, 0.32, 1], // Szybki start animacji
          }}
      >
        <div className="mx-auto flex min-h-16 items-center justify-between px-[5%] lg:grid lg:min-h-18 lg:grid-cols-[0.375fr_1fr_0.375fr] lg:gap-4 lg:px-0">
          {/* Logo */}
          <div className="flex items-center">
            <Link href="/" className="inline-flex items-center">
              <Image
                  src="/logo-d.png"
                  alt="Logo"
                  width={160}
                  height={40}
                  className="h-auto w-[40px] md:w-[60px]"
                  priority
              />
            </Link>
          </div>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex justify-center uppercase font-medium text-black">
            <ul className="flex items-center gap-6">
              {NAV_ITEMS.map((item) => (
                  <li key={item.href}>
                    <NavItemLink
                      item={item}
                      className="hover:text-accent hover:underline underline-offset-4 transition-colors px-1"
                    />
                  </li>
              ))}
            </ul>
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:block justify-self-end">
            <CtaButton />
          </div>

          {/* Mobile Toggle */}
          <div className="flex items-center lg:hidden">
            <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 relative z-[10000]"
                aria-label="Menu"
            >
              <motion.div
                  animate={{ rotate: isMobileMenuOpen ? 45 : 0, y: isMobileMenuOpen ? 8 : 0 }}
                  className="w-6 h-0.5 bg-black mb-1.5 transition-all"
              />
              <motion.div
                  animate={{ opacity: isMobileMenuOpen ? 0 : 1 }}
                  className="w-6 h-0.5 bg-black mb-1.5"
              />
              <motion.div
                  animate={{ rotate: isMobileMenuOpen ? -45 : 0, y: isMobileMenuOpen ? -8 : 0 }}
                  className="w-6 h-0.5 bg-black transition-all"
              />
            </button>
          </div>
        </div>

        {/* Panel Mobilny */}
        <AnimatePresence>
          {isMobileMenuOpen && (
              <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: '100dvh', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="lg:hidden overflow-hidden bg-primary flex flex-col items-center pt-10 gap-8"
              >
                {NAV_ITEMS.map((item) => (
                    <NavItemLink
                        key={item.href}
                        item={item}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="text-xl uppercase font-medium text-black"
                    />
                ))}
                <div className="mt-4">
                  <CtaButton />
                </div>
              </motion.div>
          )}
        </AnimatePresence>
      </motion.header>
  );
}
